import type { CSSProperties } from 'react';
import type { DialTone, DialView } from '../../core/index.js';
import { arcPath } from './geometry.js';
import { Icon } from './Icon.js';

// the ring of the app (prototype "Maschine: …"): 260 wide, radius 108, 14 thick, the same as the temperature gauge
const SIZE = 260;
const C = SIZE / 2;
const R = 108;
const STROKE = 14;
/** a round end reaches half the band (7 px) past its angle: 3.7° of the ring */
const CAP_DEG = ((STROKE / 2 / R) * 180) / Math.PI;
/** between two segments: the display's 6° plus room for both round ends */
const GAP = 6 + 2 * CAP_DEG;

/**
 * The ring while the machine does something, in the direction and order of the round display (core/screens/dial.ts):
 * a whole circle from 12 o'clock, clockwise. The shot fills it from the dark to the light brew colour (a conic gradient
 * over the filled part, so its end is always the bright one) with round ends: a dark one at 12 and a bright tip with a
 * soft glow at the end, which after the shot lies on the start of the closed ring (a little shade ahead of it, no seam
 * at 12). A lap beyond the target goes on over the start towards the heat colour (by weight) or a brighter brew (by
 * time), the tip at its end. Water fills it once, or one segment per pulse, cycle or round, each with round ends.
 * Since 23.10.2026 without ticks and without the running light (Dominik: "zu kantig … in der app kann das schon
 * moderner aussehen"). The filling and the tip glide within a frame (the caller moves the values every frame).
 * Decorative (aria-hidden); the centre carries the readable text.
 */
export function Dial({ view, testId }: { view: DialView; testId: string }) {
  const r = view.ring;
  const arc =
    r.type === 'arc'
      ? ({ '--dial-a': `${r.fill * 360}deg`, '--dial-b': `${r.over * 360}deg` } as CSSProperties)
      : undefined;
  const slot = r.type === 'segments' ? 360 / r.parts.length : 0;
  return (
    <div className={`dial dial-${r.tone}`} data-kind={view.kind}>
      <svg className="dial-svg" viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true" focusable="false">
        {r.type === 'segments' ? (
          r.parts.map((p, i) => {
            const d = arcPath(C, C, R, i * slot + GAP / 2, (i + 1) * slot - GAP / 2);
            return (
              <g key={i}>
                <path className="dial-track" d={d} strokeWidth={STROKE} />
                <path
                  className={p.current ? 'dial-part is-current' : 'dial-part'}
                  d={d}
                  pathLength={1}
                  strokeWidth={STROKE}
                  style={{ strokeDashoffset: 1 - p.fill, opacity: p.fill > 0.002 ? 1 : 0 }}
                />
              </g>
            );
          })
        ) : (
          <circle
            className={r.type === 'full' ? 'dial-full' : 'dial-track'}
            cx={C}
            cy={C}
            r={R}
            strokeWidth={STROKE}
          />
        )}
        {/* the round start under the fill, dark as the gradient's start */}
        {r.type === 'arc' && r.fill > 0 && <circle className="dial-cap" cx={C} cy={C - R} r={STROKE / 2} />}
      </svg>
      {r.type === 'arc' && (
        <>
          <div className="dial-fill" style={arc} />
          {r.over > 0 && <div className={`dial-over is-${r.overTone}`} style={arc} />}
          {r.fill > 0 && (
            // the tip turns about the ring's centre, not wrapped: past 360° it keeps going forward onto the lap beyond
            // the target
            <span
              className={r.over > 0 ? `dial-tip is-${r.overTone}` : 'dial-tip'}
              style={{ transform: `rotate(${r.marker * 360}deg)` }}
            />
          )}
        </>
      )}
      <div className="dial-center">
        <span className={`dial-label tone-${view.labelTone}`} data-testid={`${testId}-phase`}>
          {view.check && <Icon name="check" size="sm" className="dial-check" />}
          {view.label}
        </span>
        {view.icon ? (
          <Icon name={view.icon} size="xl" className="dial-icon" />
        ) : (
          view.value && (
            <span className="gauge-temp dial-value num" data-testid={`${testId}-value`}>
              {view.value}
              {(view.unit || view.sup) && (
                <span className="dial-side">
                  {view.sup && <span className="dial-sup">{view.sup}</span>}
                  {view.unit && <span className="gauge-unit">{view.unit}</span>}
                </span>
              )}
            </span>
          )
        )}
        {view.second && (
          <span className={`dial-second num tone-${view.second.tone}`} data-testid={`${testId}-second`}>
            {view.second.text}
            {view.second.sup && <span className="dial-sup is-small">{view.second.sup}</span>}
          </span>
        )}
        {view.rows.map((row, i) => (
          <span key={i} className={`dial-row num tone-${row.tone}`} data-testid={`${testId}-row${i + 1}`}>
            {row.text}
          </span>
        ))}
      </div>
    </div>
  );
}

export type { DialTone };
